import type { FetchResult } from '@apollo/client';
import * as Sentry from '@sentry/gatsby';
import React from 'react';

import { uploadFileWithProgress } from '@actions/file';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';

import {
  useCompleteAccountAvatarUploadMutation,
  useCompleteAvatarUploadMutation,
  useCompleteFhirFileUploadMutation,
  useCompletePacsFileUpload,
  useDeleteAvatarMutation,
  useInitiateAccountAvatarUploadMutation,
  useInitiateAvatarUploadMutation,
  useInitiateFhirFileUploadMutation,
  useInitiatePacsFileUpload,
  type DeleteAvatarMutation,
  type DeleteAvatarMutationVariables,
} from 'generated/file.generated';
import type { FileUploadSession } from 'generated/types';

import { usePostAttachment } from './extension';

enum UPLOAD_STEPS {
  INIT = 0.02,
  UPLOAD = 0.97,
  COMPLETE = 0.01,
}

enum UPLOAD_STEPS_PACS {
  INIT = 0.01,
  UPLOAD = 0.84,
  COMPLETE = 0.15,
}

enum UPLOAD_STEPS_ATTACHMENTS {
  INIT = 0.02,
  UPLOAD = 0.83,
  COMPLETE = 0.14,
  FINALIZE = 0.01,
}

export function useUploadFile(): [
  {
    uploadAvatar: (
      file: File,
      user_id: string,
      type: 'account' | 'user',
      logo?: boolean,
      onUploadProgress?: (progress: number) => void
    ) => Promise<void>;
    uploadAttachment: (
      resource: string,
      file: File,
      onUploadProgress?: (progress: number) => void
    ) => Promise<void>;
    uploadPacsInstance: (
      /** uuid */
      diagnostic_report_id: string,
      file: File,
      onUploadProgress?: (progress: { loaded: number; total: number }) => void
    ) => Promise<boolean | undefined>;
  },
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
  });
  const [loading, setLoading] = React.useState(false);

  // Private uploads, e.g. record attachments
  const [initiateFhirUpload] = useInitiateFhirFileUploadMutation();
  const [completeFhirUpload] = useCompleteFhirFileUploadMutation();
  const [postAttachment] = usePostAttachment();

  // DICOM uploads
  const [initiatePacsUpload] = useInitiatePacsFileUpload();
  const [completePacsUpload] = useCompletePacsFileUpload();

  // Avatar uploads
  const [initiateAvatarUpload] = useInitiateAvatarUploadMutation();
  const [completeAvatarUpload] = useCompleteAvatarUploadMutation();

  // Account avatar uploads
  const [initiateAccountAvatarUpload] =
    useInitiateAccountAvatarUploadMutation();
  const [completeAccountAvatarUpload] =
    useCompleteAccountAvatarUploadMutation();

  /**
   * Upload a profile picture for a user or an account
   * @param file File to upload
   * @param user_id ID of a user
   * @param type Owner type of profile picture
   */
  async function uploadAvatar(
    file: File,
    user_id: string,
    type: 'account' | 'user',
    logo?: boolean,
    onUploadProgress?: (progress: number) => void
  ) {
    // eslint-disable-next-line no-useless-catch
    try {
      setLoading(true);
      let progress = 0;
      onUploadProgress?.(progress);
      let fileUploadSession: FileUploadSession;
      if (type === 'account') {
        const { data } = await initiateAccountAvatarUpload({
          variables: {
            input: {
              file: {
                contentType: file.type,
                name: file.name,
                size: file.size,
              },
              fileType: logo
                ? `${user_id}/logo/`
                : `${user_id}/profile_picture/`,
            },
          },
        });
        if (!data?.initiateAccountAvatarUpload) {
          throw new Error('Internal Server Error');
        }
        progress += UPLOAD_STEPS.INIT;
        onUploadProgress?.(progress);
        fileUploadSession = data.initiateAccountAvatarUpload;
      } else {
        const { data } = await initiateAvatarUpload({
          variables: {
            input: {
              file: {
                contentType: file.type,
                name: file.name,
                size: file.size,
              },
              user_id,
            },
          },
        });
        if (!data?.initiateAvatarUpload) {
          throw new Error('Internal Server Error');
        }
        progress += UPLOAD_STEPS.INIT;
        onUploadProgress?.(progress);
        fileUploadSession = data.initiateAvatarUpload;
      }

      const {
        is_multipart,
        parts,
        multipart_upload_id,
        session_id,
        expectedHeaders,
      } = fileUploadSession;

      // Upload files
      const partModelInputs = await uploadFileWithProgress(
        file,
        parts,
        (loaded, total) =>
          onUploadProgress?.(
            progress + (loaded / total) * UPLOAD_STEPS_PACS.UPLOAD
          ),
        expectedHeaders
      );

      progress += UPLOAD_STEPS.UPLOAD;

      if (type === 'account') {
        const { data: completeAccountAvatarData, errors } =
          await completeAccountAvatarUpload({
            variables: {
              input: {
                session_id,
                multipartUploadInfo: {
                  isMultipart: is_multipart,
                  multipartUploadId: multipart_upload_id,
                  parts: is_multipart ? partModelInputs : undefined,
                },
              },
            },
            update: (cache, result) => {
              const uploadData = result.data?.completeAccountAvatarUpload;
              if (!uploadData) return;
              if (!logo) {
                cache.modify({
                  id: cache.identify({
                    __typename: 'AccountModel',
                    account_id: uploadData.account_id,
                  }),
                  fields: {
                    profile_picture: () => uploadData.profile_picture,
                  },
                });
              } else {
                cache.modify({
                  id: cache.identify({
                    __typename: 'AccountSettingModel',
                    account_id: uploadData.account_id,
                  }),
                  fields: {
                    branding_logo_url: () => uploadData.branding_logo_url,
                  },
                });
              }
            },
          });
        progress += UPLOAD_STEPS.COMPLETE;
        onUploadProgress?.(progress);
        if (errors) {
          handleError(errors, 'uploadAvatar');
        }
        if (completeAccountAvatarData?.completeAccountAvatarUpload && !errors) {
          handleSuccess(!logo ? 'uploadAvatar' : 'uploadLogo');
        }
      } else {
        const { data: completeAvatarData, errors } = await completeAvatarUpload(
          {
            variables: {
              input: {
                session_id,
                multipartUploadInfo: {
                  isMultipart: is_multipart,
                  multipartUploadId: multipart_upload_id,
                  parts: is_multipart ? partModelInputs : undefined,
                },
              },
            },
            update: (_cache, result) => {
              const uploadData = result.data?.completeAvatarUpload;
              if (!uploadData) {
                return;
              }
            },
          }
        );
        progress += UPLOAD_STEPS.COMPLETE;
        onUploadProgress?.(progress);
        if (errors) {
          handleError(errors, 'uploadAvatar');
        }
        if (completeAvatarData?.completeAvatarUpload && !errors) {
          handleSuccess('uploadAvatar');
        }
      }
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      setLoading(false);
    }
  }
  /**
   * Upload a file as a FHIR resource attachment
   * @param file File to upload
   */
  async function uploadAttachment(
    resource: string,
    file: File,
    onUploadProgress?: (progress: number) => void
  ) {
    // eslint-disable-next-line no-useless-catch
    try {
      setLoading(true);
      let progress = 0;
      onUploadProgress?.(progress);
      const { data } = await initiateFhirUpload({
        variables: {
          input: {
            contentType: file.type,
            name: file.name,
            size: file.size,
          },
        },
      });
      if (!data?.initiateFhirFileUpload) {
        throw new Error('Internal Server Error');
      }
      progress += UPLOAD_STEPS_ATTACHMENTS.INIT;
      onUploadProgress?.(progress);
      const {
        is_multipart,
        parts,
        multipart_upload_id,
        session_id,
        expectedHeaders,
      } = data.initiateFhirFileUpload;

      // Upload files
      const partModelInputs = await uploadFileWithProgress(
        file,
        parts,
        (loaded, total) =>
          onUploadProgress?.(
            progress + (loaded / total) * UPLOAD_STEPS_PACS.UPLOAD
          ),
        expectedHeaders
      );
      progress += UPLOAD_STEPS_ATTACHMENTS.UPLOAD;

      const { data: completeUploadData } = await completeFhirUpload({
        variables: {
          input: {
            session_id,
            multipartUploadInfo: {
              isMultipart: is_multipart,
              multipartUploadId: multipart_upload_id,
              parts: partModelInputs,
            },
          },
        },
      });
      progress += UPLOAD_STEPS_ATTACHMENTS.COMPLETE;
      onUploadProgress?.(progress);
      const s3_key = completeUploadData?.completeFhirFileUpload;
      if (!s3_key) {
        throw new Error('Upload Failed');
      }
      const { data: postAttachmentData, errors } = await postAttachment({
        input: {
          resource,
          s3_key,
          title: file.name,
        },
      });
      progress += UPLOAD_STEPS_ATTACHMENTS.FINALIZE;
      onUploadProgress?.(progress);
      if (errors) {
        handleError(errors, 'uploadFhirAttachment');
      }
      if (postAttachmentData?.postAttachment && !errors) {
        handleSuccess('uploadFhirAttachment');
      }
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      setLoading(false);
    }
  }

  /**
   * Upload a large PACS DICOM file
   * @param file File to upload
   */
  async function uploadPacsInstance(
    diagnostic_report_id: string,
    file: File,
    onUploadProgress?: (progress: { loaded: number; total: number }) => void
  ) {
    // eslint-disable-next-line no-useless-catch
    try {
      setLoading(true);

      const { data } = await initiatePacsUpload({
        variables: {
          input: {
            contentType: 'application/zip',
            name: file.name,
            size: file.size,
            diagnostic_report_id,
          },
        },
      });
      if (!data?.initiatePacsFileUpload) {
        throw new Error('Internal Server Error');
      }

      const {
        is_multipart,
        parts,
        multipart_upload_id,
        session_id,
        expectedHeaders,
      } = data.initiatePacsFileUpload;

      // Upload files
      const partModelInputs = await uploadFileWithProgress(
        file,
        parts,
        (loaded, total) => onUploadProgress?.({ loaded, total }),
        expectedHeaders
      );

      const response = await completePacsUpload({
        variables: {
          input: {
            session_id,
            multipartUploadInfo: {
              isMultipart: is_multipart,
              multipartUploadId: multipart_upload_id,
              parts: partModelInputs,
            },
          },
        },
      });
      const { data: completeFileUploadData, errors } = response;
      if (errors) {
        handleError(errors, 'uploadDicomFile');
      }
      if (completeFileUploadData?.completePacsFileUpload && !errors) {
        handleSuccess('uploadDicomFile');
      }
      return response.data?.completePacsFileUpload ?? undefined;
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      } else {
        Sentry.captureException(e);
      }
    } finally {
      setLoading(false);
    }
  }

  return [{ uploadAvatar, uploadAttachment, uploadPacsInstance }, loading];
}

export function useDeleteAvatar(): [
  (
    params: DeleteAvatarMutationVariables
  ) => Promise<FetchResult<DeleteAvatarMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
  });
  const [mutate, { loading: baseLoading }] = useDeleteAvatarMutation();
  const loading = baseLoading;

  async function deleteAvatar(params: DeleteAvatarMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors, 'deleteAvatar');
    }
    if (data?.deleteAvatar && !errors) {
      handleSuccess('deleteAvatar');
    }
    return response;
  }
  return [deleteAvatar, loading];
}
